import React from 'react';
import { Megaphone, Calendar, ToggleLeft, ToggleRight, Users, ShieldAlert } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { useSocietyFeatures, type SocietyFeatures } from '../../context/SocietyFeaturesContext';

interface FeatureCardProps {
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  description: string;
  audienceNote: string;
  featureKey: keyof SocietyFeatures;
  enabled: boolean;
  onToggle: (key: keyof SocietyFeatures, value: boolean) => void;
  canManage: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon, iconColor, title, description, audienceNote,
  featureKey, enabled, onToggle, canManage,
}) => (
  <div
    className="nx-card"
    style={{
      padding: '22px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: 20,
      borderLeft: `4px solid ${enabled ? iconColor : 'var(--nx-border)'}`,
      transition: 'border-color 200ms',
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 14,
        background: enabled ? `${iconColor}15` : 'var(--nx-surface-3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: enabled ? iconColor : 'var(--nx-text-muted)',
        flexShrink: 0,
        transition: 'all 200ms',
      }}
    >
      {icon}
    </div>

    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <h3
          style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: 15,
            fontWeight: 700,
            color: 'var(--nx-text-primary)',
          }}
        >
          {title}
        </h3>
        <span
          style={{
            padding: '2px 10px',
            borderRadius: 99,
            fontSize: 11,
            fontWeight: 600,
            background: enabled ? 'rgba(16,185,129,0.1)' : 'rgba(100,116,139,0.1)',
            color: enabled ? '#059669' : '#64748B',
          }}
        >
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      <p style={{ fontSize: 13, color: 'var(--nx-text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>
        {description}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--nx-text-muted)' }}>
        <Users size={11} />
        {audienceNote}
      </div>
    </div>

    {canManage ? (
      <button
        onClick={() => onToggle(featureKey, !enabled)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 4,
          borderRadius: 6,
          color: enabled ? '#059669' : 'var(--nx-text-muted)',
          flexShrink: 0,
          transition: 'color 200ms',
          display: 'flex',
          alignItems: 'center',
        }}
        title={enabled ? 'Click to disable' : 'Click to enable'}
      >
        {enabled
          ? <ToggleRight size={40} strokeWidth={1.5} />
          : <ToggleLeft size={40} strokeWidth={1.5} />
        }
      </button>
    ) : (
      <div style={{ flexShrink: 0, color: 'var(--nx-text-muted)', fontSize: 11 }}>
        Read-only
      </div>
    )}
  </div>
);

const CommunityFeaturesPage: React.FC = () => {
  const { user } = useAuth();
  const { features, setFeature } = useSocietyFeatures();
  const canManage = user?.role === 'SOCIETY_ADMIN';

  return (
    <PageLayout>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            color: 'var(--nx-text-primary)',
            letterSpacing: '-0.03em',
            marginBottom: 6,
          }}
        >
          Community Features
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
          Control which features are visible and accessible to residents of your society.
        </p>
      </div>

      {/* Permission notice for non-admins */}
      {!canManage && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            borderRadius: 10,
            background: 'rgba(245,158,11,0.06)',
            border: '1px solid rgba(245,158,11,0.2)',
            marginBottom: 20,
          }}
        >
          <ShieldAlert size={16} style={{ color: '#D97706', flexShrink: 0 }} />
          <p style={{ fontSize: 13, color: 'var(--nx-text-secondary)' }}>
            Only the <strong>Society Secretary (Admin)</strong> can enable or disable community features.
            You can view the current settings below.
          </p>
        </div>
      )}

      {/* Feature cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FeatureCard
          icon={<Megaphone size={24} />}
          iconColor="#1A6CF6"
          title="Residential Noticeboard — Resident Posting"
          description="Allow residents to post notices on the community noticeboard. All residents can always view notices. When enabled, residents can also create and submit their own notices."
          audienceNote="Resident posting ability — viewing is always available to all"
          featureKey="noticeboardEnabled"
          enabled={features.noticeboardEnabled}
          onToggle={setFeature}
          canManage={canManage}
        />
        <FeatureCard
          icon={<Calendar size={24} />}
          iconColor="#10B981"
          title="Community Events — Resident Posting"
          description="Allow residents to create community events. All residents can always view and RSVP to events. When enabled, residents can also post new events."
          audienceNote="Resident event creation ability — viewing and RSVP are always available"
          featureKey="eventsEnabled"
          enabled={features.eventsEnabled}
          onToggle={setFeature}
          canManage={canManage}
        />
      </div>

      {/* Info box */}
      <div
        style={{
          marginTop: 24,
          padding: '16px 18px',
          borderRadius: 10,
          background: 'var(--nx-surface-3)',
          border: '1px solid var(--nx-border)',
          fontSize: 12,
          color: 'var(--nx-text-muted)',
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: 'var(--nx-text-secondary)' }}>Note:</strong> All residents can always
        view notices and events. These toggles only control whether residents are permitted to
        <strong style={{ color: 'var(--nx-text-secondary)' }}> create and post</strong> new content.
        Society admins and staff can always create content regardless of these settings.
      </div>
    </PageLayout>
  );
};

export default CommunityFeaturesPage;
